<!-- Breadcrumb solo en modo página -->
@if (!drawerMode) {
<app-page-breadcrumb [pageTitle]="isEditing ? 'Editar Ticket' : 'Nuevo Ticket'"></app-page-breadcrumb>
}

<!-- Modo drawer: form directo | Modo página: con card wrapper -->
@if (drawerMode) {
<div class="p-6">
  <ng-container *ngTemplateOutlet="formContent" />
</div>
} @else {
<div class="grid grid-cols-1 gap-6">
  <app-component-card [title]="isEditing ? 'Editar Ticket' : 'Nuevo Ticket'" desc="Complete los datos del ticket">
    <ng-container *ngTemplateOutlet="formContent" />
  </app-component-card>
</div>
}

<ng-template #formContent>
    <!-- Loading -->
    @if (loading) {
      <div class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
        <span class="ml-3 text-gray-500 dark:text-gray-400">Cargando datos...</span>
      </div>
    }

    <!-- Error -->
    @if (error) {
      <div class="mb-4 p-4 bg-error-50 border border-error-200 rounded-lg dark:bg-error-500/10 dark:border-error-500/20">
        <div class="flex items-center gap-3">
          <svg class="w-5 h-5 text-error-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <p class="text-error-600 dark:text-error-400">{{ error }}</p>
        </div>
      </div>
    }

    <!-- Aviso de duplicados (INC 17) -->
    @if (duplicateWarning && duplicateWarning.length > 0) {
      <div class="mb-4 p-4 rounded-lg border border-warning-300 bg-warning-50 dark:border-warning-500/30 dark:bg-warning-500/10">
        <div class="flex items-start gap-3">
          <svg class="w-5 h-5 text-warning-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z"></path>
          </svg>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-semibold text-warning-800 dark:text-warning-300">Posibles tickets duplicados</p>
            <p class="text-xs text-warning-700 dark:text-warning-400 mb-3">
              El contacto ya tiene tickets abiertos con un asunto similar. Revíselos antes de continuar.
            </p>
            <div class="space-y-2">
              @for (dup of duplicateWarning; track dup.id) {
                <div class="flex items-center justify-between gap-2 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 px-3 py-2">
                  <div class="min-w-0">
                    <p class="text-xs font-mono text-gray-400">{{ dup.ticketNumber }}</p>
                    <p class="text-sm text-gray-900 dark:text-white truncate">{{ dup.subject }}</p>
                    @if (dup.statusName) {
                      <span class="inline-block mt-0.5 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">{{ dup.statusName }}</span>
                    }
                  </div>
                  <button type="button" (click)="abrirTicketExistente(dup)"
                    class="flex-shrink-0 px-3 py-1.5 text-xs rounded-lg font-medium bg-brand-500 text-white hover:bg-brand-600">
                    Ver / abrir
                  </button>
                </div>
              }
            </div>
            <div class="flex justify-end gap-2 mt-3">
              <button type="button" (click)="descartarDuplicados()"
                class="px-3 py-1.5 text-sm rounded-lg font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700">
                Cancelar
              </button>
              <button type="button" (click)="crearDeTodosModos()" [disabled]="saving"
                class="px-3 py-1.5 text-sm rounded-lg font-medium bg-warning-500 text-white hover:bg-warning-600 disabled:opacity-50">
                Crear de todos modos
              </button>
            </div>
          </div>
        </div>
      </div>
    }

    <!-- Formulario -->
    @if (!loading) {
      <form [formGroup]="form" (ngSubmit)="guardar()" class="space-y-5">

        <!-- Asunto -->
        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Asunto <span class="text-error-500">*</span>
          </label>
          <input
            type="text"
            formControlName="subject"
            placeholder="Asunto del ticket"
            maxlength="255"
            class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
            [class.border-error-500]="hasError('subject')"
            [class.border-gray-300]="!hasError('subject')"
          />
          @if (hasError('subject')) {
            <p class="mt-1.5 text-sm text-error-500">{{ getError('subject') }}</p>
          }
        </div>

        <!-- Descripción -->
        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Descripción
          </label>
          <textarea
            formControlName="description"
            placeholder="Describa el ticket con detalle..."
            rows="4"
            class="dark:bg-dark-900 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 resize-none"
          ></textarea>
        </div>

        <!-- Pipeline y Etapa -->
        <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Pipeline</label>
            <select
              formControlName="idPipeline"
              class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800">
              <option [ngValue]="null">{{ placeholderFor('pipeline') }}</option>
                @for (pipeline of pipelines; track pipeline.id) {
                  <option [ngValue]="pipeline.id">{{ pipeline.name }}</option>
                }
            </select>
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Etapa</label>
            <select
              formControlName="idStage"
              class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed">
              <option [ngValue]="null">
                {{ form.get('idPipeline')?.value ? 'Seleccione una etapa' : 'Seleccione un pipeline' }}
              </option>
              @for (stage of stages; track stage.id) {
                <option [ngValue]="stage.id">{{ stage.name }}</option>
              }
            </select>
          </div>
        </div>

        <!-- Prioridad -->
        <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Prioridad</label>
            <select
              formControlName="idTicketPriority"
              class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800">
              <option [ngValue]="null">{{ placeholderFor('prioridad') }}</option>
              @for (priority of priorities; track priority.id) {
                <option [ngValue]="priority.id">{{ priorityLabel(priority) }}</option>
              }
            </select>
          </div>
        </div>

        <!-- Categoría y Source -->
        <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Categoría</label>
            <select
              formControlName="idTicketCategory"
              class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800">
              <option [ngValue]="null">Sin categoría</option>
              @for (cat of categoryTree; track cat.id) {
                <option [ngValue]="cat.id" [disabled]="cat.hasChildren">
                  {{ categoryIndentFor(cat.depth) }}{{ cat.name }}{{ cat.hasChildren ? ' (subcategorías)' : '' }}
                </option>
              }
            </select>
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Origen
              @if (isEditing) {
                <span class="text-xs text-gray-400 ml-1">(no editable)</span>
              }
            </label>
            <select
              formControlName="source"
              class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800 disabled:opacity-60 disabled:cursor-not-allowed">
              @for (src of sources; track src) {
                <option [ngValue]="src">{{ originLabel(src) }}</option>
              }
            </select>
          </div>
        </div>

        <!-- Asignado a -->
        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Asignado a <span class="text-error-500">*</span>
          </label>
          <select
            formControlName="intIdUserOwner"
            class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
            [class.border-error-500]="hasError('intIdUserOwner')"
            [class.border-gray-300]="!hasError('intIdUserOwner')" >
            <option [ngValue]="null">{{ placeholderFor('usuario asignado') }}</option>
            @for (u of users; track u.id) {
              <option [ngValue]="u.id">{{ u.fullname }} {{ u.lastname }}</option>
            }
          </select>
          @if (hasError('intIdUserOwner')) {
            <p class="mt-1.5 text-sm text-error-500">{{ getError('intIdUserOwner') }}</p>
          }
        </div>

        <!-- ======== Contacto Asociado ======== -->
        <div class="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div class="flex items-center gap-2 mb-4">
            <p class="text-sm font-semibold text-gray-700 dark:text-gray-300">Contacto Asociado</p>
            @if (contactAutoFilled) {
              <span class="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-success-50 text-success-700 dark:bg-success-500/10 dark:text-success-400">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                Autocompletado
              </span>
            }
          </div>

          @if (!selectedContact) {
            <div class="relative">
              <label class="mb-1.5 block text-xs font-medium text-gray-600 dark:text-gray-400">
                Buscar contacto por nombre o email
              </label>
              <div class="relative">
                <input
                  type="text"
                  [value]="contactSearchTerm"
                  (input)="onContactSearchInput($any($event.target).value)"
                  (blur)="hideContactDropdownDelayed()"
                  placeholder="Escriba al menos 2 caracteres para buscar..."
                  class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500 pr-8"
                />
                @if (searchingContacts) {
                  <div class="absolute right-2.5 top-2.5">
                    <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500"></div>
                  </div>
                }
              </div>

              @if (showContactDropdown && contactSearchResults.length > 0) {
                <div class="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  @for (contact of contactSearchResults; track contact.id) {
                    <button type="button" (click)="selectContact(contact)"
                      class="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 text-left transition-colors">
                      <div class="flex-shrink-0 flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold {{ getContactAvatarColor(contact) }}">
                        {{ getContactInitials(contact) }}
                      </div>
                      <div class="min-w-0 flex-1">
                        <p class="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {{ contact.fullname }} {{ contact.lastname }}
                        </p>
                        @if (contact.mail) {
                          <p class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ contact.mail }}</p>
                        }
                      </div>
                    </button>
                  }
                </div>
              }
            </div>
          } @else {
            <div class="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <div class="flex-shrink-0 flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold {{ getContactAvatarColor(selectedContact) }}">
                {{ getContactInitials(selectedContact) }}
              </div>
              <div class="min-w-0 flex-1">
                <p class="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {{ selectedContact.fullname }} {{ selectedContact.lastname }}
                </p>
                <div class="flex items-center gap-2 mt-0.5 flex-wrap">
                  @if (selectedContact.mail) {
                    <span class="text-xs text-gray-500 dark:text-gray-400">{{ selectedContact.mail }}</span>
                  }
                  @if (selectedContact.phone) {
                    <span class="text-xs text-gray-400 dark:text-gray-500">·</span>
                    <span class="text-xs text-gray-500 dark:text-gray-400">{{ selectedContact.phone }}</span>
                  }
                </div>
              </div>
              <button type="button" (click)="quitarContacto()" title="Quitar contacto"
                class="px-3 py-1 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          }
        </div>

        <!-- ======== Empresa Asociada ======== -->
        <div class="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div class="flex items-center gap-2 mb-4">
            <p class="text-sm font-semibold text-gray-700 dark:text-gray-300">Empresa Asociada</p>
            @if (companyAutoFilled) {
              <span class="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-success-50 text-success-700 dark:bg-success-500/10 dark:text-success-400">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                Autocompletado
              </span>
            }
          </div>

          @if (!selectedCompany) {
            <div class="relative">
              <label class="mb-1.5 block text-xs font-medium text-gray-600 dark:text-gray-400">
                Buscar empresa por nombre
              </label>
              <div class="relative">
                <input
                  type="text"
                  [value]="companySearchTerm"
                  (input)="onCompanySearchInput($any($event.target).value)"
                  (blur)="hideCompanyDropdownDelayed()"
                  placeholder="Escriba al menos 2 caracteres para buscar..."
                  class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500 pr-8"
                />
                @if (searchingCompanies) {
                  <div class="absolute right-2.5 top-2.5">
                    <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500"></div>
                  </div>
                }
              </div>

              @if (showCompanyDropdown && companySearchResults.length > 0) {
                <div class="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  @for (company of companySearchResults; track company.id) {
                    <button type="button" (click)="selectCompany(company)"
                      class="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 text-left transition-colors">
                      <div class="flex-shrink-0 flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold {{ getCompanyAvatarColor({id: company.id, commercialName: company.commercialName}) }}">
                        {{ getCompanyInitials({id: company.id, commercialName: company.commercialName}) }}
                      </div>
                      <div class="min-w-0 flex-1">
                        <p class="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {{ company.commercialName }}
                        </p>
                        @if (company.phone) {
                          <p class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ company.phone }}</p>
                        }
                      </div>
                    </button>
                  }
                </div>
              }
            </div>
          } @else {
            <div class="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <div class="flex-shrink-0 flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold {{ getCompanyAvatarColor(selectedCompany) }}">
                {{ getCompanyInitials(selectedCompany) }}
              </div>
              <div class="min-w-0 flex-1">
                <p class="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {{ selectedCompany.commercialName }}
                </p>
                @if (selectedCompany.phone) {
                  <p class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ selectedCompany.phone }}</p>
                }
              </div>
              <button type="button" (click)="quitarEmpresa()" title="Quitar empresa"
                class="px-3 py-1 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          }
        </div>

        <!-- Botones -->
        <div class="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <app-button variant="outline" type="button" (btnClick)="cancelar()">
            Cancelar
          </app-button>
          <app-button type="button" [disabled]="saving" (btnClick)="guardar()">
            @if (saving) {
              <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            }
            {{ isEditing ? 'Actualizar' : 'Guardar' }}
          </app-button>
        </div>
      </form>
    }

    <!-- INC 3: modal de elección de empresa (el contacto tiene varias asociadas) -->
    @if (showCompanyChoiceModal) {
      <div class="fixed inset-0 z-modal flex items-center justify-center p-4">
        <div class="absolute inset-0 bg-black/40" (click)="skipCompanyChoice()"></div>
        <div class="relative w-full max-w-md rounded-2xl bg-white dark:bg-gray-900 shadow-2xl border border-gray-200 dark:border-gray-700">
          <div class="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 class="text-base font-semibold text-gray-900 dark:text-white">Selecciona la empresa</h3>
            <button type="button" (click)="skipCompanyChoice()"
              class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-300 dark:hover:bg-gray-800">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          <div class="p-5 space-y-2 max-h-80 overflow-y-auto">
            <p class="text-sm text-gray-500 dark:text-gray-400 mb-2">
              Este contacto está asociado a varias empresas. Elige la que corresponde al ticket.
            </p>
            @for (assoc of contactCompaniesForModal; track assoc.id) {
              <button type="button" (click)="selectCompanyInModal(assoc)"
                class="w-full flex items-center justify-between gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                <span class="text-sm font-medium text-gray-900 dark:text-white truncate">{{ companyChoiceName(assoc) }}</span>
                @if (assoc.isPrimary) {
                  <span class="flex-shrink-0 text-xs px-2 py-0.5 rounded-full bg-brand-50 text-brand-600 dark:bg-brand-500/10 dark:text-brand-400">Principal</span>
                }
              </button>
            }
          </div>
          <div class="flex justify-end px-5 py-4 border-t border-gray-200 dark:border-gray-700">
            <button type="button" (click)="skipCompanyChoice()"
              class="px-3 py-1.5 text-sm rounded-lg font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700">
              Elegir manualmente
            </button>
          </div>
        </div>
      </div>
    }
</ng-template>
